import { callFinnhub } from "./finnhubClient";

// ─── The next earnings report for one company ────────────────────────────────
// Finnhub's earnings calendar, filtered to the symbol, from today forward.
// The plan the app runs on returns analyst consensus on these rows
// (epsEstimate, revenueEstimate — verified 2026-09-22: 616 of 892 rows in a
// 30-day window carried EPS), so the estimates are shown when present and
// simply absent when not. Nothing is filled in.
//
// Returns null when nothing is known (no answer — the caller shows nothing),
// { none: true } when Finnhub answered and no report is scheduled in the
// window, otherwise { date, hour, epsEstimate, revenueEstimate, quarter, year }.
//
// The whole-market calendar is capped by Finnhub at 1,500 rows, newest date
// first, so a 120-day pull without the symbol filter silently drops the
// nearer reports (AAPL at ~5 weeks out was missing while NVDA at 8 weeks was
// in — 2026-09-22). The proxy therefore passes the symbol through; and if an
// answer ever arrives unfiltered, a missing symbol there is "unknown", not
// "none".

const _cache = {};
const TTL = 24 * 60 * 60 * 1000;
const WINDOW_DAYS = 120;

const iso = d => d.toISOString().slice(0, 10);

export async function fetchNextReport(symbol) {
  const s = String(symbol || "").toUpperCase().trim();
  if (!s) return null;
  const now = Date.now();
  const hit = _cache[s];
  if (hit && now - hit.ts < TTL) return hit.data;

  const from = iso(new Date(now));
  const to   = iso(new Date(now + WINDOW_DAYS * 24 * 60 * 60 * 1000));
  const d = await callFinnhub("earnings", { from, to, symbol: s });
  const rows = Array.isArray(d?.calendar?.earningsCalendar) ? d.calendar.earningsCalendar : null;
  if (!rows) return null;                       // no answer → nothing cached, nothing claimed

  const mine = rows.filter(r => r.symbol === s && typeof r.date === "string" && r.date >= from);
  if (!mine.length && rows.some(r => r.symbol !== s)) return null;   // unfiltered, truncated: unknown
  const next = mine.sort((a, b) => a.date.localeCompare(b.date))[0] || null;
  const data = next ? {
    date:            next.date,
    hour:            next.hour || "",
    epsEstimate:     Number.isFinite(next.epsEstimate)     ? next.epsEstimate     : null,
    revenueEstimate: Number.isFinite(next.revenueEstimate) ? next.revenueEstimate : null,
    quarter:         next.quarter ?? null,
    year:            next.year ?? null,
  } : { none: true };
  _cache[s] = { ts: now, data };
  return data;
}

// Finnhub's `hour` is one of bmo / amc / dmh / "" — before the open, after
// the close, during market hours, or unknown.
export const HOUR_HE = { bmo: "לפני פתיחת המסחר", amc: "אחרי סגירת המסחר", dmh: "במהלך המסחר" };

